import {addIntent, createIntentMap, forEachIntent} from '../intent';
import {$revision, $subview} from './constants';
import {dynamic} from './dynamic';
import {isViewConfigIntent} from './ConfigIntent';
import {getPriority} from './priority';
import type {View} from './types';
import type {IntentSource} from './inspection';
import {debug} from './inspection';

export function assignObjectProps<Props>(target: Props, source: Props): Props {
  const keys = Object.keys(source as object) as (keyof Props)[];
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    switch (key) {
      case 'css':
      case 'inherits':
      case 'subviews':
      case 'uses':
        // eslint-disable-next-line no-continue
        continue;
      default:
        target[key] = source[key];
    }
  }
  return target;
}

export function createViewRevision<Props>(
  instance: View.ViewInstance<Props>,
  Component: string | React.ComponentType<React.PropsWithChildren<Props>>,
  props: Props,
  forwardedRef: View.InferRef<Props>,
  isElement: boolean,
  debugEnabled = false,
  displayName = '',
): View.ViewRevision<Props> {
  const revision = createIntentMap({
    Component,
    initialProps: props,
    instance,
    props: {},
  }) as View.MutableViewRevision<Props>;

  if (debugEnabled) {
    revision.inspection = debug.createInspection(revision, displayName);
  }

  (revision.props as {ref?: View.InferRef<Props>}).ref = forwardedRef;

  assignObjectProps(revision.props, props);

  if (!isElement) {
    (revision.props as unknown as {inherits?: View.Inherits}).inherits = {
      [$revision]: revision,
    };
  }

  return revision;
}

function getInherits<Props>(props: Props) {
  return (props as {inherits?: View.Inherits | View.Subview<Props>})?.inherits;
}

export function getViewRevisionFromProps<Props>(
  props: Props,
): View.ViewRevision<Props> | void {
  return (getInherits(props) as View.Inherits)?.[$revision];
}
export function getSubviewIntentsFromProps<Props>(
  props: Props,
): View.SubviewIntents<Props> | void {
  return (getInherits(props) as View.Subview<Props>)?.[$subview];
}

export function addIntentsToRevision<Props>(
  revision: View.ViewRevision<Props>,
  intents: void | (View.Intent<Props> | null | undefined | false)[],
  priority: View.Priority,
  source?: IntentSource | readonly (IntentSource | undefined)[],
  throwWhenPriorityMismatch = false,
): void {
  if (!intents?.length) {
    return;
  }
  const {inspection} = revision;
  const getSourceAtIndex = inspection
    ? debug.createSourceLookup(revision, intents, source)
    : undefined;
  forEachIntent(intents, (intent, index) => {
    const intentSource = getSourceAtIndex?.(index);
    const intentPriority = getPriority(intent);
    if (intentPriority !== priority) {
      if (throwWhenPriorityMismatch && intentPriority > priority) {
        if (
          process.env.NODE_ENV !== 'production' ||
          !dynamic.isIntent(intent)
        ) {
          throw new Error(
            'Attempted to run an intent at a lower priority than expected.',
          );
        } else {
          // assignProps is now run at a higher priority, but to avoid breaking
          // in production, we'll run at normal priority for backwards compat.
          // We'll continue to throw in dev mode to highlight paths that need
          // to be updated.
          const resolved = intent(revision.props);
          const resolvedSource = inspection
            ? debug.createSourceFromDynamic(revision, resolved, intentSource)
            : undefined;
          addIntentsToRevision(revision, resolved, priority, resolvedSource);
          // eslint-disable-next-line no-console
          console.warn('An intent was run at a lower priority than expected.');
        }
      }
      return;
    }

    if (dynamic.isIntent(intent)) {
      const resolved = intent(revision.props);
      const resolvedSource = inspection
        ? debug.createSourceFromDynamic(revision, resolved, intentSource)
        : undefined;
      addIntentsToRevision(revision, resolved, priority, resolvedSource, true);
    } else if (isViewConfigIntent(revision, intent)) {
      addIntentsToRevision(revision, intent, priority, intentSource, true);
    } else {
      if (inspection) {
        // Record before the runtime merge can discard earlier values.
        debug.recordIntent(revision, intent, priority, intentSource);
      }
      addIntent(revision, intent);
    }
  });
}
